import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { Footer, MarketingHeader, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { requireDivorceProduct } from '@/lib/divorce-gate';
import { DIVORCE_JURISDICTIONS } from '@/lib/divorce';

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Do I have to pay for divorce forms?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Blank court forms are public documents published by your state courts for free. You pay a court filing fee when you file (fee waivers are available in every state if you qualify based on income).',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this legal advice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Harbor provides educational information and links to official court resources. For advice about your specific situation, consult a family law attorney licensed in your state.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is an uncontested divorce?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A divorce where both spouses agree (or are working to agree) on all major terms: property, support, and parenting. Uncontested cases can usually be completed with court self-help forms, without hiring a lawyer for full representation.',
      },
    },
  ],
};

export const Route = createFileRoute('/divorce/')({
  beforeLoad: requireDivorceProduct,
  component: DivorceHubPage,
});

function DivorceHubPage() {
  useEffect(() => {
    track('Divorce Hub Viewed');
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Free Divorce Checklist by State | Harbor'
        description='State-by-state uncontested divorce checklists: residency rules, official court forms, filing fees, fee waivers, and what happens after you file. Free and educational.'
        path='/divorce'
        jsonLd={faqJsonLd}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h1 className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'>
            Divorce, step by step — for your state
          </h1>
          <p className='mx-auto max-w-2xl text-xl leading-relaxed text-gray-600'>
            Every state publishes free official divorce forms. Pick your state for the
            residency rules, filing fees, fee waivers, and a plain-English checklist from
            first form to final judgment.
          </p>
          <p className='mx-auto mt-4 max-w-2xl text-sm text-gray-500'>
            Educational only — not legal advice. Harbor links to official court sources.
          </p>
        </div>
      </section>

      <section className='mx-auto max-w-5xl px-6 py-12'>
        <h2 className='mb-6 text-2xl font-bold text-gray-900'>Choose your state</h2>
        <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4'>
          {DIVORCE_JURISDICTIONS.map((j) => (
            <Link
              key={j.code}
              to='/divorce/$state'
              params={{ state: j.slug }}
              onClick={() => track('Divorce Hub State Clicked', { state: j.code })}
              className='flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-gray-800 transition-colors hover:border-primary hover:text-primary'
            >
              <span className='font-medium'>{j.name}</span>
              <FiArrowRight className='shrink-0' />
            </Link>
          ))}
        </div>
      </section>

      <section className='bg-gray-50 py-12'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h2 className='mb-3 text-2xl font-bold text-gray-900'>
            Have children? Start with your parenting plan.
          </h2>
          <p className='mx-auto mb-6 max-w-2xl text-gray-600'>
            Courts expect a parenting plan in any divorce with minor children. Build one
            free, then bring it to your forms.
          </p>
          <Link
            to='/parenting-plan'
            onClick={() => track('Divorce Hub Parenting Plan CTA Clicked')}
            className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90'
          >
            Build your parenting plan
            <FiArrowRight />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
